import { useAuth } from "@/contexts/AuthContext";
import { Clock, Shield, LogOut, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

export default function PendingApprovalPage() {
  const { signOut, user, accountStatus } = useAuth();

  const isRejected = accountStatus === "rejected";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-6 text-center"
      >
        {/* Icon */}
        <div className="flex justify-center">
          <div className={`h-20 w-20 rounded-2xl flex items-center justify-center ${
            isRejected
              ? "bg-destructive/10 border border-destructive/30"
              : "bg-warning/10 border border-warning/30"
          }`}>
            {isRejected ? (
              <Shield className="h-10 w-10 text-destructive" />
            ) : (
              <Clock className="h-10 w-10 text-warning" />
            )}
          </div>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {isRejected ? "Account Not Approved" : "Account Under Review"}
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {isRejected
              ? "Your account registration has been reviewed and was not approved at this time. If you believe this is an error, please contact support."
              : "Your email has been verified successfully! Your account is now awaiting approval by our team. You will receive access once your account is approved."
            }
          </p>
        </div>

        {/* Status Steps */}
        {!isRejected && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3 text-left">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-success/10 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-4 w-4 text-success" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Email Verified</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-warning/10 flex items-center justify-center shrink-0 animate-pulse">
                <Clock className="h-4 w-4 text-warning" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Awaiting Approval</p>
                <p className="text-xs text-muted-foreground">Our team will review your account shortly</p>
              </div>
            </div>
            <div className="flex items-center gap-3 opacity-40">
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Approval Notification</p>
                <p className="text-xs text-muted-foreground">You'll be notified when approved</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={signOut} className="w-full gap-2">
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>

        {/* Footer */}
        <p className="text-[11px] text-muted-foreground/60">
          Powered by <span className="font-semibold text-primary">Vazoul</span>
        </p>
      </motion.div>
    </div>
  );
}
